import type { appliedFilter, list, LOCATION, showFilters } from "./FilterInterface";

type FilterConfig = {
  title: string;
  label: string;
  filterKey: keyof appliedFilter;
  showFilterKey: keyof showFilters;
  optionKey: keyof list;
  idKey: string;
  nameKey: string;
  isMulti?: boolean;
  transformOptions?: boolean;
  enableSearch?: boolean;
  getTitle?: (filterName?: string) => string;
  getLabel?: (lableName?:string) => string;

  // Paginated Select
  usePaginatedSelect?: boolean;
};

export const FILTER_CONFIG: Record<string, FilterConfig> = {
  user: {
    title: "By Member",
    label: "Member",
    filterKey: "userIds",
    showFilterKey: "user",
    optionKey: "userList",
    idKey: "UserId",
    nameKey: "UserName",
  },

  lead: {
    title: "By Customer",
    label: "Customer",
    filterKey: "leadIds",
    showFilterKey: "lead",
    optionKey: "customerList",
    idKey: "Id",
    nameKey: "LeadName",
    usePaginatedSelect: true,
  },

  distributor: {
    title: "By Distributor",
    label: "Distributor",
    filterKey: "distributorIds",
    showFilterKey: "distributor",
    optionKey: "distributorList",
    idKey: "Id",
    nameKey: "Name",
  },

  tag: {
    title: "By Tag",
    label: "Tag",
    filterKey: "tagIds",
    showFilterKey: "tag",
    optionKey: "tagList",
    idKey: "Id",
    nameKey: "TagName",
  },

  status: {
    title: "By Status",
    label: "Status",
    filterKey: "statusIds",
    showFilterKey: "status",
    optionKey: "statusList",
    idKey: "StatusId",
    nameKey: "Status",
  },

  source: {
    title: "By Source",
    label: "Source",
    filterKey: "sourceIds",
    showFilterKey: "source",
    optionKey: "sourceList",
    idKey: "Id",
    nameKey: "Source",
  },

  stage: {
    title: "By Stage",
    label: "Stage",
    filterKey: "stageIds",
    showFilterKey: "stage",
    optionKey: "stageList",
    idKey: "Id",
    nameKey: "Name",
  },

  leadType: {
    title: "By Type",
    label: "Type",
    filterKey: "typeIds",
    showFilterKey: "leadType",
    optionKey: "typeList",
    idKey: "Id",
    nameKey: "TypeName",
  },

  territory: {
    title: "By Territory",
    label: "Territory",
    filterKey: "territoryIds",
    showFilterKey: "territory",
    optionKey: "territoryList",
    idKey: "TerritoryId",
    nameKey: "TerritoryName",
  },

  referral: {
    title: "By Referral",
    label: "Referral",
    filterKey: "referralIds",
    showFilterKey: "referral",
    optionKey: "referralList",
    idKey: "referralUserId",
    nameKey: "userName",
  },

  createdBy: {
    title: "Created By",
    label: "Created By",
    filterKey: "createdByUserIds",
    showFilterKey: "createdBy",
    optionKey: "createdByList",
    idKey: "UserId",
    nameKey: "UserName",
  },

  assignedTo: {
    title: "Assigned To",
    label: "Assigned To",
    filterKey: "assignedToUserIds",
    showFilterKey: "assignedTo",
    optionKey: "assignedToList",
    idKey: "UserId",
    nameKey: "UserName",
  },

  department: {
    title: "By Department",
    label: "Department",
    filterKey: "departmentIds",
    showFilterKey: "department",
    optionKey: "departmentList",
    idKey: "Id",
    nameKey: "Name",
  },

  designation: {
    title: "By Designation",
    label: "Designation",
    filterKey: "designationIds",
    showFilterKey: "designation",
    optionKey: "designationList",
    idKey: "Id",
    nameKey: "Name",
  },

  branch: {
    title: "By Branch",
    label: "Branch",
    filterKey: "branchIds",
    showFilterKey: "branch",
    optionKey: "branchList",
    idKey: "Id",
    nameKey: "Name",
  },

  role: {
    title: "By Role",
    label: "Role",
    filterKey: "roleIds",
    showFilterKey: "role",
    optionKey: "roleList",
    idKey: "Id",
    nameKey: "Name",
  },

  meetingType: {
    title: "Meeting Objective",
    label: "Meeting Objective",
    filterKey: "meetingTypeIds",
    showFilterKey: "meetingType",
    optionKey: "meetingTypeList",
    idKey: "Id",
    nameKey: "VisitPurpose",
  },

  locationType: {
    title: "By Location Type",
    label: "Location Type",
    filterKey: "locationTypes",
    showFilterKey: "locationType",
    optionKey: "locationTypeList",
    idKey: "Id",
    nameKey: "Name",
    transformOptions: true,
  },

  category: {
    title: "By Category",
    label: "Category",
    filterKey: "categoryIds",
    showFilterKey: "category",
    optionKey: "categoryList",
    idKey: "Id",
    nameKey: "Name",
  },

  mapType: {
    title: "By Map Type",
    label: "Map Type",
    filterKey: "markerType",
    showFilterKey: "mapType",
    optionKey: "mapTypeOptions",
    idKey: "value",
    nameKey: "label",
    enableSearch: false,
  },

  statusType: {
    title: "By Status",
    label: "Status",
    filterKey: "statusType",
    showFilterKey: "statusType",
    optionKey: "statusList",
    idKey: "value",
    nameKey: "label",
    getTitle: (filterName) => filterName === "users" ? "By User Status" : "By Status",
  },

  locationId: {
    title: "By Location Name",
    label: "Location",
    filterKey: "locationId",
    showFilterKey: "locationId",
    optionKey: "locationList",
    idKey: "LocationId",
    nameKey: "LocationName",
  },

  leaveType: {
    title: "By Leave Type",
    label: "Leave Type",
    filterKey: "leaveTypeId",
    showFilterKey: "leaveType",
    optionKey: "leaveTypeList",
    idKey: "Id",
    nameKey: "Type",
  },

  manager: {
    title: "By Manager",
    label: "Manager",
    filterKey: "managerId",
    showFilterKey: "manager",
    optionKey: "managerList",
    idKey: "Id",
    nameKey: "Name",
  },

  leadContacts: {
    title: "By Contact Person",
    label: "Contact Person",
    filterKey: "leadContacts",
    showFilterKey: "leadContacts",
    optionKey: "contactList",
    idKey: "Id",
    nameKey: "Name",
  },

  separationMode: {
    title: "By Separation Mode",
    label: "Separation Mode",
    filterKey: "separationMode",
    showFilterKey: "separationMode",
    optionKey: "separationMode",
    idKey: "Id",
    nameKey: "SeparationType",
  },

  oldDeviceType: {
    title: "Old Device Type",
    label: "Old Device Type",
    filterKey: "oldDeviceTypeId",
    showFilterKey: "oldDeviceType",
    optionKey: "DeviceTypeList",
    idKey: "Id",
    nameKey: "Type",
  },

  newDeviceType: {
    title: "New Device Type",
    label: "New Device Type",
    filterKey: "newDeviceTypeId",
    showFilterKey: "newDeviceType",
    optionKey: "DeviceTypeList",
    idKey: "Id",
    nameKey: "Type",
  },

  taskType: {
    title: "By Task Type",
    label: "Task Type",
    filterKey: "taskType",
    showFilterKey: "taskType",
    optionKey: "taskType",
    idKey: "value",
    nameKey: "label",
  },

  dueDate: {
    title: "By Due Date",
    label: "Due Date",
    filterKey: "dueDate",
    showFilterKey: "dueDate",
    optionKey: "dueDate",
    idKey: "value",
    nameKey: "label",
  },

  byLeadStatus: {
    title: "By Lead Activity",
    label: "Lead Status",
    filterKey: "byLeadStatus",
    showFilterKey: "byLeadStatus",
    optionKey: "leadStatusList",
    idKey: "StatusId",
    nameKey: "Status",
    enableSearch: false,
    getTitle: (filterName) => filterName === "timeline" ? "By Activity" : "By Lead Status",
    getLabel :(filterName) => filterName === "timeline" ? "Activity":"Lead Status",
  },

  group: {
    title: "By Group",
    label: "Group",
    filterKey: "groupId",
    showFilterKey: "group",
    optionKey: "groupList",
    idKey: "Id",
    nameKey: "GroupName",
  },

  userGeofenceStatus: {
    title: "By User Geofence Status",
    label: "User Geofence Status",
    filterKey: "userGeofenceStatus",
    showFilterKey: "userGeofenceStatus",
    optionKey: "userGeofenceStatusList",
    idKey: "value",
    nameKey: "label",
  },

  trackingStatus: {
    title: "By Tracking Status",
    label: "Status",
    filterKey: "trackingStatus",
    showFilterKey: "trackingStatus",
    optionKey: "trackingStatusList",
    idKey: "value",
    nameKey: "label",
    enableSearch: false,
  },
};

type SpecialSelectConfig = {
  title: string;
  label: string;
  filterKey: keyof appliedFilter;
  showFilterKey: keyof showFilters;
  optionSource: string;
  idKey: string;
  nameKey: string;
  disabled?: (filters: appliedFilter) => boolean;
};

export const SPECIAL_SELECT_CONFIG: Record<string, SpecialSelectConfig> = {
  type: {
    title: "By Type",
    label: "Type",
    filterKey: "typeId",
    showFilterKey: "type",
    optionSource: "typeIdsList",
    idKey: "TypeId",
    nameKey: "TypeName",
    disabled: (filters: appliedFilter) => !filters?.groupId?.Id,
  },

  item: {
    title: "By Item",
    label: "Item",
    filterKey: "assetItemIds",
    showFilterKey: "item",
    optionSource: "assetItemIdsList",
    idKey: "AssetItemId",
    nameKey: "AssetName",
    disabled: (filters: appliedFilter) => !filters?.typeId?.TypeId,
  },
};

type DateFilterConfig = {
  title: string;
  label: string;
  filterKey: keyof appliedFilter;
  showFilterKey: keyof showFilters;
};

export const DATE_FILTER_CONFIG: Record<string, DateFilterConfig> = {
  allocatedDate: {
    title: "By Allocated Date",
    label: "Allocated Date",
    showFilterKey: "allocatedDate",
    filterKey: "allocatedDate",
  },

  returnDate: {
    title: "By Returned Date",
    label: "Returned Date",
    showFilterKey: "returnDate",
    filterKey: "returnDate",
  },
};

type TextFilterConfig = {
  title: string;
  filterKey: keyof appliedFilter;
  showFilterKey: keyof showFilters;
  type: string;
  placeholder: string;
};

export const TEXT_FILTER_CONFIG: Record<string, TextFilterConfig> = {
  orderNumber: {
    title: "By Order Number",
    filterKey: "orderNumber",
    showFilterKey: "orderNumber",
    type: "number",
    placeholder: "Order Number",
  },

  minAmount: {
    title: "By Minimum Amount",
    filterKey: "minValue",
    showFilterKey: "minAmount",
    type: "number",
    placeholder: "Min Value",
  },

  maxAmount: {
    title: "By Maximum Amount",
    filterKey: "maxValue",
    showFilterKey: "maxAmount",
    type: "number",
    placeholder: "Max Value",
  },
};

type GeoFenceFilterConfig = {
  type: string;
  title: string;
  label: string;
  filterKey: keyof appliedFilter;
  showFilterKey: keyof showFilters;
  idKey: string;
  nameKey: string;
};

export const GEOFENCE_FILTER_CONFIG: GeoFenceFilterConfig[] = [
  {
    type: "geoFenceType",
    title: "By PunchIn GeofenceType",
    filterKey: "punchInType",
    showFilterKey: "geoFenceType",
    label: "PunchIn",
    idKey: "Id",
    nameKey: "Name",
  },
  {
    type: "geoFenceType",
    title: "By PunchOut GeofenceType",
    filterKey: "punchOutType",
    showFilterKey: "geoFenceType",
    label: "PunchOut",
    idKey: "Id",
    nameKey: "Name",
  },
  {
    type: "geoFenceName",
    title: "By PunchIn GeofenceName",
    filterKey: "punchInName",
    showFilterKey: "geoFenceName",
    label: "PunchIn",
    idKey: "Id",
    nameKey: "Name",
  },
  {
    type: "geoFenceName",
    title: "By PunchOut GeofenceName",
    filterKey: "punchOutName",
    showFilterKey: "geoFenceName",
    label: "PunchOut",
    idKey: "Id",
    nameKey: "Name",
  },
];

type LocationField = {
  key: keyof LOCATION;
  label: string;
  idKey: string;
  nameKey: string;
  dependsOn?: keyof LOCATION;
  reset?: (keyof LOCATION)[];
  optionSource: string;
  show: (showFilters?: showFilters) => boolean;
};

export const LOCATION_FIELDS: LocationField[] = [
  {
    key: "country",
    label: "Country",
    idKey: "CountryId",
    nameKey: "Name",
    reset: ["state", "city", "locality"],
    optionSource: "countryList",
    show: (showFilters) => !!showFilters?.location?.country?.show,
  },
  {
    key: "state",
    label: "State",
    idKey: "StateId",
    nameKey: "Name",
    dependsOn: "country",
    reset: ["city", "locality"],
    optionSource: "stateList",
    show: (showFilters) => !!showFilters?.location?.state?.show,
  },
  {
    key: "city",
    label: "City",
    idKey: "CityId",
    nameKey: "Name",
    dependsOn: "state",
    reset: ["locality"],
    optionSource: "cityList",
    show: (showFilters) => !!showFilters?.location?.city?.show,
  },
  {
    key: "locality",
    label: "Locality",
    idKey: "LocalityId",
    nameKey: "LocalityName",
    dependsOn: "city",
    optionSource: "localityList",
    show: (showFilters) => !!showFilters?.location?.locality?.show,
  },
];